import { sqliteTable, text, integer, index, primaryKey } from 'drizzle-orm/sqlite-core';

export const slashCommands = sqliteTable('slash_commands', {
  id:                text('id').primaryKey(),
  guildId:           text('guild_id').notNull(),
  name:              text('name').notNull().unique(),
  description:       text('description').notNull(),
  embedJson:         text('embed_json').notNull(),
  enabled:           integer('enabled', { mode: 'boolean' }).notNull().default(true),
  createdBy:         text('created_by').notNull(),
  createdAt:         integer('created_at').notNull(),
  updatedAt:         integer('updated_at').notNull(),
  // Phase 4 additions (migration 0003):
  registeredAt:      integer('registered_at'),       // nullable ms epoch; null = pending or failed Discord registration
  lastRegisterError: text('last_register_error'),    // nullable; populated with error text on bulk-PUT failure
  // Phase 7 additions (migration 0007): type discriminator for the dispatcher.
  // 'static' (default) = D1 embed_json + zero handler logic — every pre-Phase-7 row.
  // 'dynamic' = handler computes embed at runtime (e.g. /next-raid, /membercount).
  // 'utility' = handler executes a side-effect and returns its own embed (/role-toggle).
  // 'admin'   = officer-gated commands (e.g. /reload).
  type:              text('type', { enum: ['static', 'dynamic', 'utility', 'admin'] }).notNull().default('static'),
});

export const reminders = sqliteTable('reminders', {
  id:               text('id').primaryKey(),
  guildId:          text('guild_id').notNull(),
  channelId:        text('channel_id').notNull(),
  name:             text('name').notNull(),
  embedJson:        text('embed_json').notNull(),
  scheduleCron:     text('schedule_cron'),
  triggerType:      text('trigger_type', { enum: ['cron', 'event', 'both'] }).notNull().default('cron'),
  enabled:          integer('enabled', { mode: 'boolean' }).notNull().default(true),
  lastPostedAt:     integer('last_posted_at'),
  failedAt:         integer('failed_at'),
  retryAfter:       integer('retry_after'),
  createdBy:        text('created_by').notNull(),
  createdAt:        integer('created_at').notNull(),
  updatedAt:        integer('updated_at').notNull(),
  // Phase 4 additions (migration 0004): event-trigger binding fields
  eventId:          text('event_id'),                                        // nullable Discord event snowflake
  eventMatchMode:   text('event_match_mode', { enum: ['specific', 'all'] }), // nullable
  eventLeadMinutes: integer('event_lead_minutes'),                            // nullable minutes before event
  // Phase 5 additions (migration 0006): auto-disable failure tracking
  failureCount:     integer('failure_count').notNull().default(0),
  disabledReason:   text('disabled_reason', { enum: ['manual', 'auto_failure'] }),
});

export const eventsCache = sqliteTable('events_cache', {
  eventId:            text('event_id').primaryKey(),
  guildId:            text('guild_id').notNull(),
  name:               text('name').notNull(),
  status:             text('status', { enum: ['SCHEDULED', 'ACTIVE', 'COMPLETED', 'CANCELED'] }).notNull(),
  scheduledStartTime: integer('scheduled_start_time').notNull(),
  channelId:          text('channel_id'),
  rawJson:            text('raw_json').notNull(),
  syncedAt:           integer('synced_at').notNull(),
});

export const auditLog = sqliteTable(
  'audit_log',
  {
    id:          text('id').primaryKey(),
    guildId:     text('guild_id').notNull(),
    actorId:     text('actor_id').notNull(),
    action:      text('action').notNull(),
    targetType:  text('target_type'),
    targetId:    text('target_id'),
    payloadJson: text('payload_json'),
    ts:          integer('ts').notNull(),
  },
  (table) => ({
    // Phase 4 (migration 0005): index for per-reminder history queries
    // (target_type='reminder' AND target_id=X ORDER BY ts DESC LIMIT 10).
    // Also benefits 04-03's audit log scans by target_type.
    targetIdx: index('audit_log_target_idx').on(table.targetType, table.targetId, table.ts),
  }),
);

export const guildConfig = sqliteTable('guild_config', {
  guildId:   text('guild_id').primaryKey(),
  timezone:  text('timezone').notNull(),
  updatedAt: integer('updated_at').notNull(),
});

// Phase 7 additions (migration 0008): per-guild toggleable role list backing /role-toggle (CMD-07).
// Officers manage rows via /api/toggleable-roles (wired in plan 07-04). Members invoke
// /role-toggle and pick a row from the autocomplete list — the handler (plan 07-03) flips
// the role on the invoking member.
export const guildToggleableRoles = sqliteTable('guild_toggleable_roles', {
  guildId:   text('guild_id').notNull(),
  roleId:    text('role_id').notNull(),
  label:     text('label').notNull(),
  position:  integer('position'),                     // optional ordering hint for the autocomplete list
  createdAt: integer('created_at').notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.guildId, table.roleId] }),
}));
